import { FC, useCallback, useEffect, useState } from 'react'
import DOMPurify from 'isomorphic-dompurify'

import { colors } from '../../theme'
import Highlight from '../Highlight'
import ActionButton, { ActionButtonProps } from '../ActionButton'
import { ContentCardProps } from '../ContentCard'
import { ContentCardGroupProps } from '../ContentCardGroup'
import ContentCards from '../ContentCards'
import Icon from '../Icon'
import { ECollapsable } from '../Collapsable/types'

import * as SC from './styled'

type FallbackProps = {
  noMenuText?: string
  suggestionText?: string
  changeMindText?: string
  menuButtonProps?: ActionButtonProps
}

export type PlanningMenuSectionProps = {
  className?: string
  title?: string
  items?: (ContentCardProps | ContentCardGroupProps)[]
  fallback?: FallbackProps
}

export type hideMenusProps = {
  hidden?: boolean
  isSelectedDayAWeekEndDay?: boolean
}

const PlanningMenuSection: FC<PlanningMenuSectionProps & hideMenusProps> = ({
  className,
  title,
  items,
  fallback,
  hidden,
  isSelectedDayAWeekEndDay,
}) => {
  const checkIsToDisplay = useCallback(() => {
    if (!items) return false
    else if (hidden && isSelectedDayAWeekEndDay) {
      return true
    } else if (hidden && !isSelectedDayAWeekEndDay) return false
    else if (!hidden) return true
  }, [hidden, isSelectedDayAWeekEndDay, items])

  useEffect(() => {
    setCheckToDisplay(checkIsToDisplay)
  }, [checkIsToDisplay, fallback, isSelectedDayAWeekEndDay])

  const [toDisplay, setCheckToDisplay] = useState(checkIsToDisplay)
  return (
    <>
      <SC.ScrollTarget className={className} />
      <SC.Collapse
        header={
          title && (
            <SC.SectionTitle>
              <Highlight
                backgroundColor={colors.greenMint}
                color={colors.pureBlack}
                text={title}
              />
            </SC.SectionTitle>
          )
        }
        isCollapsable={ECollapsable.BOTH}
        isOpenOnInit={hidden ? false : true}
      >
        <SC.Inner>
          {items && items?.length > 0 && toDisplay && (
            <ContentCards items={items} />
          )}
          {!toDisplay && (
            <SC.EmptyMenu>
              <SC.NoMenuText>{fallback?.noMenuText}</SC.NoMenuText>
              {fallback?.suggestionText && (
                <SC.Suggestion>
                  <SC.SuggestionIcon>
                    <Icon icon="plateLove" />
                  </SC.SuggestionIcon>
                  <SC.SuggestionText
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(fallback?.suggestionText),
                    }}
                  />
                </SC.Suggestion>
              )}
              {fallback?.changeMindText && (
                <SC.ChangeMyMind>{fallback?.changeMindText}</SC.ChangeMyMind>
              )}
              {fallback?.menuButtonProps && (
                <ActionButton {...fallback.menuButtonProps} />
              )}
            </SC.EmptyMenu>
          )}
        </SC.Inner>
      </SC.Collapse>
    </>
  )
}

export default PlanningMenuSection
